/**
 * Authorized fetch utility - wrapper around fetch that adds auth headers
 *
 * IMPORTANT: This is a legacy utility that directly accesses localStorage.
 * It's kept for backwards compatibility with existing code.
 *
 * For NEW code, use one of these patterns instead:
 * 1. Create a hook that uses useAuth() and makes fetch calls
 * 2. Use api functions from lib/api.ts
 * 3. Pass token explicitly as a parameter
 *
 * @param url - API endpoint URL
 * @param options - Fetch options
 * @param skipAccountHeader - Skip account header (for account-agnostic endpoints)
 * @param skipAuthHeader - Skip auth header (for public endpoints)
 */
export async function authorizedFetch(
    url: string,
    options: RequestInit = {},
    skipAccountHeader: boolean = false,
    skipAuthHeader: boolean = false
): Promise<Response> {
    let token = localStorage.getItem('auth_token');

    // Prepend API base URL if the URL is relative
    const apiBaseUrl =
        (import.meta as any).env?.VITE_HYDRANAUT_API_URL ||
        (import.meta as any).env?.VITE_API_URL ||
        '';
    const fullUrl = url.startsWith('http') ? url : `${apiBaseUrl}${url}`;

    const headers = new Headers(options.headers);
    if (token && !skipAuthHeader) {
        headers.set('Authorization', `Bearer ${token}`);
    } 
    
    // Add account ID header from user object in localStorage
    if (!skipAccountHeader) {
        const userStr = localStorage.getItem('user');
        if (userStr) {
            try {
                const user = JSON.parse(userStr);
                if (user.accountId) {
                    headers.set('X-Account-Id', user.accountId);
                }
            } catch (e) {
                console.error('Failed to parse user', e);
            }
        }
    }
    
    headers.set('Content-Type', 'application/json');

    let response = await fetch(fullUrl, {
        ...options,
        headers,
    });

    // If we get a 401, clear auth and redirect to login
    if (response.status === 401 && !skipAuthHeader) {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user');
        localStorage.removeItem('token_expires_at');
        window.location.href = '/login';
    }

    if (!response.ok) {
        try {
            const errorText = await response.text();
            let errorData: any = {};
            
            try {
                errorData = JSON.parse(errorText);
            } catch {
                // Not JSON, use text as error
                errorData = { error: errorText };
            }
            
            // Create structured error object for error-handler
            const error: any = new Error(errorData.error || errorData.message || 'Request failed');
            error.response = {
                status: response.status,
                data: errorData
            };
            
            throw error;
        } catch (error: any) {
            // If error already has response structure, rethrow it
            if (error.response) {
                throw error;
            }
            
            // Otherwise create structured error
            const structuredError: any = new Error('Request failed');
            structuredError.response = {
                status: response.status,
                data: { error: error.message }
            };
            throw structuredError;
        }
    }

    return response;
}

export async function json<T = any>(response: Response): Promise<T> {
    return response.json();
}
